import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataManager } from '@syncfusion/ej2-data';
import { Observable, forkJoin } from 'rxjs';
import { concatMap, map, tap } from "rxjs/operators";
import { Themes } from 'src/app/models/formations/domaine';
import { IChampPersoViewModel } from 'src/app/pages/champs-perso/models/champ-perso-models';
import { JwtUrlAdaptor } from '../../adaptors/jwt-url-adaptor';
import { ODataAdaptor } from '../../adaptors/odata-adaptor';
import { ModeleEvaluationModelSimple } from '../../models/evaluations/modele.evaluation.model.simple';
import { CategorieFormationModel } from "../../models/formations/categorie-formation-model";
import { CodeDiplome } from "../../models/formations/code-diplome";
import { FormFormationModel } from '../../models/formations/formFormation.model';
import { FormationSimpleModel } from '../../models/formations/formation-simple.model';
import { FormationForFormationCompose, FormationModel } from '../../models/formations/formation.model';
import { FormationProgrammeModel } from '../../models/formations/formationProgramme.model';
import { ValeurListeFixeModel } from '../../models/listes-fixes/valeur-liste-fixe.model';
import { ToastSuccesModel } from '../../models/toasts/toast-succes.model';
import { ConfigService } from '../config/config.service';
import { NotificationToastService } from '../notifications/notification-toast.service';
import { LocalStorageTokenService } from '../utilisateurs/local-storage-token.service';

@Injectable({
  providedIn: 'root',
})
export class FormationsService {
  private readonly baseUrlApi = this.configService.config.urlApi;;
  public test
  constructor(
    private oDataAdaptor: ODataAdaptor,
    private jwtUrlAdaptor: JwtUrlAdaptor,
    private client: HttpClient,
    private notificationToastService: NotificationToastService,
    private http: HttpClient,
    private notificationService: NotificationToastService,
    private configService: ConfigService,
    private localStorageTokenService: LocalStorageTokenService,
  ) {}

  addFormation(formation: FormationModel): Observable<number> {
    return this.http
      .put<number>(`${this.baseUrlApi}/api/f/formations/addFormation`, formation)
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('La formation a bien été créée.'))));
  }

  addFormationWithProgramme(formation: FormFormationModel): Observable<number> {
    let id = null;
    return this.addFormation(formation.formation).pipe(
      concatMap(idFormation => {
        id = idFormation;
        const programme = formation.formationProgramme;
        programme.idFormation = idFormation;
        return this.addProgramme(programme);
      }),
      map(() => {
        return id;
      }),
    );
  }

  getCodesDiplomes(inclureElementVide: boolean = true): Observable<CodeDiplome[]> {
    return this.http.get<CodeDiplome[]>(`${this.baseUrlApi}/api/f/formations/getCodesDiplomes`)
      .pipe(map(liste => inclureElementVide ? [...liste, {id: null, code: null, libelle: null}]: liste));
  }

  updateFormation(formation: FormationModel): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrlApi}/api/f/formations/updateFormation`, formation)
      .pipe(tap(() => this.notificationService.push(new ToastSuccesModel('La formation a été modifiée.'))));
  }

  updateProgramme(programme: FormationProgrammeModel): Observable<void> {
    return this.http.post<void>(`${this.baseUrlApi}/api/f/formations/UpdateFormationProgramme`, programme);
  }

  getFormationWithProgramme(idFormation: number): Observable<FormFormationModel> {
    const formation = this.http.get<FormationModel>(`${this.baseUrlApi}/api/f/formations/getFormation/` + idFormation);
    const formationProgramme = this.http.get<FormationProgrammeModel>(
      `${this.baseUrlApi}/api/f/formations/getFormationProgramme/` + idFormation,
    );
    return forkJoin({formation, formationProgramme}).pipe(map(v => Object.assign(new FormFormationModel(), v)));
  }

  getFormationSansProgramme(idFormation: number): Observable<FormationModel> {
    return this.http.get<FormationModel>(`${this.baseUrlApi}/api/f/formations/getFormation/` + idFormation);
  }

  activerOuDesactiverFormation(formation: FormationModel, active: boolean) {
    return this.http
      .post<void>(`${this.baseUrlApi}/api/f/formations/updateActiveFormation`, {Id: formation.id, Active: active})
      .pipe(
        tap(() => (formation.active = active)),
        tap(() =>
          this.notificationToastService.push(
            new ToastSuccesModel(active ? `Formation activée` : `Formation désactivée`),
          ),
        ),
      );
  }

  loadAnneesReferentiel(idFormation: number, avecElementVide: boolean = true) : Observable<number[]>{
    const result =  this.http.get<number[]>(`${this.baseUrlApi}/api/f/ReferentielMatieres/getAnneesReferentiel/${idFormation}`);
    if (avecElementVide) {
     return result.pipe(map(annees => [...annees, null]))
    }
    return result;
  }

  chargerEtatsFormation(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeEtatsFormation/getValeurs`);
  }

  chargerFamillesFormation(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeDomainesFormation/getValeurs`);
  }

  chargerThemesByDomainFormation(idDomain: number): Observable<Themes[]> {
    return this.http.get<Themes[]>(`${this.baseUrlApi}/api/f/Themes/GetThemesForDomaine/${idDomain}`);
  }

  chargerModalitesFormation(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeModalitesFormation/getValeurs`);
  }

  chargerNiveauxDiplomesFormation(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(
      `${this.baseUrlApi}/api/f/ListeFixeNiveauxDiplomeFormation/getValeurs`,
    );
  }

  chargerTypeQualificationVisee(): Observable<ValeurListeFixeModel[]> {
    return this.http.get<ValeurListeFixeModel[]>(
      `${this.baseUrlApi}/api/f/ListeTypeQualificationVisee/getValeurs`,
    );
  }

  chargerCategoriesFormation(): Observable<CategorieFormationModel[]> {
    return this.http.get<CategorieFormationModel[]>(
      `${this.baseUrlApi}/api/f/formations/getallformationscategories`,
    );
  }

  public chargerFormationsSimple(): Observable<FormationSimpleModel[]> {
    return this.http.get<FormationSimpleModel[]>(`${this.baseUrlApi}/api/f/formations/getFormationsSimple`);
  }

  public chargerFormationsComposees(): Observable<FormationSimpleModel[]> {
    return this.http.get<FormationSimpleModel[]>(`${this.baseUrlApi}/api/f/formations/getFormationsComposees`);
  }

  buildDataManagerGetFormations(): DataManager {
    return new DataManager({
      url: `${this.baseUrlApi}/api/f/formationsview`,
      adaptor: this.oDataAdaptor
    });
  }

  private addProgramme(programme: FormationProgrammeModel): Observable<void> {
    return this.http.put<void>(`${this.baseUrlApi}/api/f/formations/AddFormationProgramme`, programme);
  }

  updateFormationWithProgramme(formModifiee: FormFormationModel): Observable<any> {
    return this.updateFormation(formModifiee.formation).pipe(
      concatMap(() => {
        return this.updateProgramme(formModifiee.formationProgramme);
      }),
    );
  }

  getFormationModelesEvaluations(formationId: number, target?: number) :Observable<ModeleEvaluationModelSimple[]>{
    let url = `${this.baseUrlApi}/api/f/formations/getFormationsModelesEvaluations/${formationId}`;
    if (target !== undefined) {
      url += `?target=${target}`;
    }
    return this.http.get<ModeleEvaluationModelSimple[]>(url);
  }

  getModelesEvaluation(idFormation: number, text: string, type: number[], target?: number):Observable<ModeleEvaluationModelSimple[]>{
    let path = `api/f/formationsModelesEvaluationView/getModelesEvaluations/${idFormation}?$orderby=Titre`;
    if (target !== undefined) {
      path += `&Target=${target}`;
    }
    const searchText = text ? `contains(tolower(cast(Titre, 'Edm.String')), '${text}')`: null;
    const filterResult = type.length > 0 ? `Type in (${type})` : 'Type in ()';

    if(searchText){
      path += `&$filter=(${searchText} and ${filterResult})`;
    } else {
      path += `&$filter=(${filterResult})`;
    }

    return this.http.get<ModeleEvaluationModelSimple[]>(`${this.baseUrlApi}/${path}`);
  }

  updateFormationModelesEvaluations(idFormation: number, allModeles: ModeleEvaluationModelSimple[], messageSuccess: string, target: number): Observable<any> {
    return this.http.post(`${this.baseUrlApi}/api/f/formations/updateFormationModelesEvaluation?target=${target}`, {idFormation : idFormation, modelesEvaluations: allModeles})
      .pipe(tap(() => this.notificationService.push(new ToastSuccesModel(messageSuccess))));
  }

  upgradeVersionModeleEvaluationForFormation(idFormation: number, idModele: number): Observable<number> {
    return this.http.post<number>(`${this.baseUrlApi}/api/f/formations/UpgradeModeleEvaluationForFormation`, {idFormation, idModele})
      .pipe(tap(() => this.notificationToastService.push(new ToastSuccesModel('Le modèle a été mis à jour en dernière version.'))))
  }

  public getStates(): Observable<ValeurListeFixeModel[]>{
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeEtatsFormation/getValeurs`)
  }

  public getSessionsStates(): Observable<ValeurListeFixeModel[]>{
    return this.http.get<ValeurListeFixeModel[]>(`${this.baseUrlApi}/api/f/ListeFixeEtatsSession/getValeurs`)
  }

  public getChampsAdditionnels(): Observable<IChampPersoViewModel[]>{
    return this.client.get<IChampPersoViewModel[]>(`${this.baseUrlApi}/api/f/ChampsPersoFormation/GetChamps`)
  }

  public getFormationsSessionsForParcours(idParcours: number, params: number): Observable<FormationForFormationCompose[]> {
    return this.client.get<FormationForFormationCompose[]>(`${this.baseUrlApi}/api/f/SessionsForParcoursView/GetSelection/${idParcours}?progression=${params}`)
  }

  public searchSessionForParcours(idParcours: number, progression: number, reference?: string | null): Observable<FormationForFormationCompose[]> {
    return this.client.get<FormationForFormationCompose[]>(`${this.baseUrlApi}/api/f/SessionsForParcoursView/GetSelection/${idParcours}?reference=${reference}&progression=${progression}`);
  }

  public duplicateFormation(formation: FormationModel): Observable<FormationModel>{
    return this.http.put<FormationModel>(`${this.baseUrlApi}/api/f/formations/DuplicateFormation/${formation.id}`, formation);
  }
}
